import { Component } from 'react'
import moment from 'moment-timezone'
import { checkDealProposalsFollowUpDates } from 'components/Deal/utils'
import debounce from 'lodash/debounce'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { connect } from 'react-redux'
import { makeGetRequest, makePatchRequest } from 'utils/api'
import update from 'immutability-helper'
import { fromContacts } from '../serializers'
import differenceBy from 'lodash/differenceBy'
import isEqual from 'lodash/isEqual'
import swal from 'sweetalert'
import { getTimezone } from 'utils/authService'
import { Details } from './Details'
import { withUnsavedChanges, withSweetAlert } from 'hocs'
import { FollowupModalContainer } from 'components/common/modals/FollowupModalContainer'
import { CloneOpportunityModal } from './CloneOpportunityModal'
import { LinkOpportunityModal } from './LinkOpportunityModal'
import { formatResponse, formatErrorMessage } from 'utils/formatting'

const timezone = getTimezone()
const tomorrow = moment(new Date(), timezone).add(1, 'days').toDate()

const EnhancedDetails = withUnsavedChanges(Details)

const projectQuoteNumberMapper = {
  quote: {
    property: 'quoteNumber',
    url: '/v1/deals/next-quote-number'
  },
  project: {
    property: 'projectNumber',
    url: '/v1/deals/next-project-number'
  }
}

@connect()
class DealDetails extends Component {
  state = {
    deal: {
      address: '',
      address2: '',
      street2: '',
      status: '',
      dealCategory: null,
      dealSource: null,
      noWinReason: null,
      teams: [],
      dealScopes: []
    },
    dealAwarded: false,
    requiredFields: ['name'],
    error: false,
    initialFullRender: true,
    internalTeam: {
      user: '',
      role: ''
    },
    showDealFollowup: false,
    followUpStatus: '',
    showHealthError: false,
    showFollowUpByEmailModal: false,
    showCloneOpportunityModal: false,
    showLinkOpportunityModal: false,
    usingQuoteNumberAutomation: false,
    usingProjectNumberAutomation: false
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.deal.id !== prevState.deal.id) {
      return {
        ...prevState,
        deal: { ...nextProps.deal, followUpDate: nextProps.deal.followUpDate || tomorrow },
        dealAwarded: nextProps.deal.awardedCompany && !!nextProps.deal.awardedCompany
      }
    }
    return null
  }

  async componentDidMount() {
    const { clients } = this.props.deal
    if (clients) {
      this.getClients(clients)
    }

    const { projectNumberAutomation, quoteNumberAutomation } = await makeGetRequest(
      '/v1/deals/automation-number-status'
    )
    this.setState({
      usingQuoteNumberAutomation: quoteNumberAutomation,
      usingProjectNumberAutomation: projectNumberAutomation
    })
  }

  componentDidUpdate(prevProps) {
    const { clients } = prevProps.deal
    const { clients: newClients } = this.props.deal

    if (clients !== undefined && this.state.initialFullRender) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.getClients(clients)
    } else if (
      clients !== undefined &&
      newClients !== undefined &&
      newClients.length > clients.length
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.getClients(clients)
    }
    if (
      prevProps.deal.status !== undefined &&
      prevProps.deal.status.value !== this.props.deal.status.value
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        deal: this.props.deal,
        dealAwarded: this.props.deal.awardedCompany && !!this.props.deal.awardedCompany
      })
    }
    if (prevProps.deal.awardedCompany !== this.props.deal.awardedCompany) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        dealAwarded: this.props.deal.awardedCompany && !!this.props.deal.awardedCompany
      })
    }
    if (!isEqual(prevProps.deal, this.props.deal)) {
      this.setState({
        deal: this.props.deal
      })
    }
    if (prevProps.deal.healthError !== this.props.deal.healthError) {
      this.setState({
        showHealthError: this.props.deal.healthError
      })
    }
  }

  _generateQuoteProjectNumber = async (type, dispatch, shouldUpdateStore) => {
    const { property, url } = projectQuoteNumberMapper[type]
    const { nextNumber } = await makeGetRequest(url)
    this.changeProperty(nextNumber, property, shouldUpdateStore)
    if (nextNumber) {
      try {
        await makePatchRequest(`/v1/deals/${this.state.deal.id}`, {
          [property]: nextNumber
        })
      } catch (error) {
        dispatch &&
          dispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'There was an issue updating Opportunity',
              message: formatResponse(formatErrorMessage(error)),
              level: 'error'
            }
          })
      }
    }
  }

  generateQuoteNumber = async () => {
    this._generateQuoteProjectNumber('quote')
  }

  generateProjectNumber = async dispatch => {
    this._generateQuoteProjectNumber('project', dispatch, true)
  }

  getClients = (clients, all = true) => {
    this.setState(
      {
        ...this.state,
        initialFullRender: false
      },
      async () => {
        if (all) {
          let newState
          let clientsUpdated = 0
          for (let index = 0; index < clients.length; index++) {
            ;(async (client, index) => {
              if (this.state.deal.clients[index].contacts === undefined) {
                const url = `/v1/contacts/company/${client.value}`
                const contacts = await makeGetRequest(url)
                newState = update(newState || this.state, {
                  deal: {
                    clients: {
                      [index]: {
                        contacts: { $set: fromContacts(contacts) }
                      }
                    }
                  }
                })
                clientsUpdated++
                if (clientsUpdated === clients.length) {
                  this.setState(newState)
                }
              }
            })(clients[index], index)
          }
        } else {
          const url = `/v1/contacts/company/${clients[0].value}`
          const contacts = await makeGetRequest(url)
          const index = this.state.deal.clients.findIndex(c => c.value === clients[0].value)
          const newState = update(this.state, {
            deal: {
              clients: {
                [index]: {
                  contacts: { $set: fromContacts(contacts) }
                }
              }
            }
          })
          this.setState(newState)
        }
      }
    )
  }

  updateState = (value, property, cb, shouldUpdateStore) => {
    const dealValue = this.state.deal[property]
    if ((!dealValue && dealValue !== 0) || dealValue.toString() !== value) {
      this.setState(
        {
          ...this.state,
          deal: {
            ...this.state.deal,
            [property]: value
          }
        },
        () => {
          this.props.setIsDealFormDirty()
          if (shouldUpdateStore) {
            this.props.updateDealProperty(property, value)
          }
        }
      )

      cb && cb(dealValue)
    }
  }

  changeProperty = (value, property, shouldUpdateStore) => {
    this.updateState(value, property, null, shouldUpdateStore)
  }

  changeSpecialClientsProperty = async (values, property, shouldUpdateStore = false) => {
    if (this.state.deal[property].length > values.length) {
      const willRemove = await swal({
        title: 'Are you sure you want to remove this company from this opportunity?',
        text: 'This action can’t be undone',
        buttons: ['Cancel', 'Yes'],
        icon: 'warning'
      })
      if (willRemove) {
        this.updateState(values, property, null, shouldUpdateStore)
      }
    } else {
      this.updateState(values, property, null, shouldUpdateStore)
    }
  }

  toggleHealthErrorModal = () => {
    this.setState(prevState => ({
      showHealthError: !prevState.showHealthError
    }))
  }

  changeStatus = value => {
    let newValue = value
    if (typeof value === 'object') {
      newValue = value.value
    }

    const oldStatus = this.props.dealsStatuses.find(
      status => status.code.toLowerCase() === this.state.deal.status.value.toLowerCase()
    )

    if (this.state.deal.status.value === newValue) {
      return
    }
    if (value.categoryCode.toLowerCase() === 'won') {
      if (!this.props.proposals.length || oldStatus.category.code.toLowerCase() === 'won') {
        this.props.toggleContractorAwardedModal({ isWonByUs: true, newDealStatus: newValue })
      } else {
        if (['USER_DISABLED', 'SYSTEM_DISABLED'].includes(this.props.settings?.statusAutomation)) {
          this.changeProperty(value, 'status')
          return
        }

        swal(
          'This opportunity has proposals',
          'Please go to the Proposals tab, move proposals in the appropriate status and the opportunity will be automatically updated'
        )
      }
    } else if (value.categoryCode.toLowerCase() === 'lost') {
      if (
        !this.props.proposals.length ||
        (oldStatus.category.code.toLowerCase() === 'lost' &&
          (!['NN', 'YN'].includes(newValue) ||
            !['NN', 'YN'].includes(this.state.deal.status.value)))
      ) {
        this.props.toggleLostOpportunityModal({
          newDealStatus: newValue
        })
      } else {
        if (['USER_DISABLED', 'SYSTEM_DISABLED'].includes(this.props.settings?.statusAutomation)) {
          this.changeProperty(value, 'status')
          return
        }

        swal(
          'This opportunity has proposals',
          oldStatus.category.code.toLowerCase() === 'won'
            ? 'Please go to the proposals tab and mark all of them as lost by us or by clients. The status of the opportunity will be automatically updated'
            : 'Please go to the Proposals tab, move proposals in the appropriate status and the opportunity will be automatically updated'
        )
      }
    } else {
      if (
        ['won', 'lost'].includes(oldStatus.category.code.toLowerCase()) &&
        this.props.proposals.length
      ) {
        if (['USER_DISABLED', 'SYSTEM_DISABLED'].includes(this.props.settings?.statusAutomation)) {
          this.changeProperty(value, 'status')
          return
        }

        swal(
          'This opportunity has proposals',
          'Please go to the Proposals tab, move proposals in the appropriate status and the opportunity will be automatically updated'
        )
        return
      }
      if (newValue === 'CONTRACTORAWARDED') {
        this.props.toggleContractorAwardedModal({ newDealStatus: newValue })
      } else if (value.categoryCode.toLowerCase() === 'followup') {
        this.setState(prevState => ({
          showDealFollowup: !prevState.showDealFollowup,
          followUpStatus: newValue,
          deal: {
            ...this.state.deal,
            status: value
          }
        }))
      } else {
        this.changeProperty(value, 'status')
      }
    }
  }

  toggleFollowupDateModal = () => {
    this.setState(prevState => ({
      showDealFollowup: !prevState.showDealFollowup
    }))
  }

  updateDealStatusFollowupDate = followUpDate => {
    this.changeFollowUp(followUpDate, true)
  }

  changeFollowUp = async date => {
    if (checkDealProposalsFollowUpDates(date, this.props.proposals)) {
      const willChangeFollowUp = await swal({
        title: `One or more proposals have a different follow-up date.
          This new date will apply to all proposals from this opportunity.`,
        text: 'Are you sure you want to continue?',
        buttons: ['Cancel', 'Yes'],
        icon: 'warning'
      })
      if (willChangeFollowUp) {
        this.changeProperty(date, 'followUpDate')
        this.props.updateDeal({ ...this.state.deal, followUpDate: date }, () => ({
          type: 'DEAL_UPDATE_PROPOSALS_FOLLOWUP_REQUESTED',
          payload: {
            id: this.state.deal.id,
            followUpDate: date
          }
        }))
        this.props.setIsDealFormPristine()
      }
    } else {
      this.changeProperty(date, 'followUpDate')
    }
  }

  changeProperties = partialState => {
    const newDeal = { ...this.state.deal, ...partialState }
    this.setState(
      {
        ...this.state,
        deal: newDeal
      },
      () => {
        this.props.setIsDealFormDirty()
      }
    )
  }

  validateRequired = async () => {
    const { dispatch } = this.props
    await this.setState({
      error: false
    })
    this.state.requiredFields.forEach(field => {
      if (
        !this.state.deal[field] ||
        (Array.isArray(this.state.deal[field]) && !this.state.deal[field].length)
      ) {
        this.setState({
          error: true
        })
        dispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'All required fields must have values',
            level: 'error'
          }
        })
      }
    })
  }

  onSave = debounce(async cb => {
    const { deal } = this.state
    await this.validateRequired()
    if (!this.state.error) {
      this.props.updateDeal(deal)
      this.props.setIsDealFormPristine()
      cb && cb()
    }
  }, 500)

  onSelectCallback = params => {
    const { address, city, state, latitude, longitude, country, zipCode, street } = params

    this.setState(
      {
        ...this.state,
        deal: {
          ...this.state.deal,
          city,
          latitude,
          longitude,
          address,
          address2: street,
          state,
          country,
          zipCode
        }
      },
      () => {
        this.props.setIsDealFormDirty()
      }
    )
  }

  scheduleTaskForAwardedCompany = ({ value }) => {
    const existingClientsIds = this.state.deal.dealCompanies.map(c => c.value)
    if (!existingClientsIds.includes(value)) {
      this.props.toggleShowScheduleTaskForAwardedCompany()
    }
  }

  refreshClientContacts = async id => {
    const url = `/v1/contacts/company/${id}`
    const contacts = await makeGetRequest(url)
    const index = this.state.deal.clients.findIndex(c => c.value === id)
    const newState = update(this.state, {
      deal: {
        clients: {
          [index]: {
            contacts: { $set: fromContacts(contacts) }
          }
        }
      }
    })

    this.setState(newState)
  }

  removeClientConfirmation = async (callback, client) => {
    const isClientAwarded = this.state.deal.awardedCompany?.value === client[0].value
    const sweetAlertFactory = withSweetAlert(
      <div>
        {isClientAwarded && (
          <p style={{ color: 'red' }}>This client is marked as awarded for this opportunity</p>
        )}
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
        All documents, proposals, comments, notes & tasks related to this client will be permanently
        deleted.
      </div>
    )
    sweetAlertFactory(
      {
        title: 'Are you sure you want to remove this client from this opportunity?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      () => {
        callback()
      },
      { cancelConfirmation: false }
    )
  }

  changeClientContacts = async value => {
    let state
    const { value: id, label } = value.slice(-1)[0]
    const client = {
      value: id,
      label,
      dueDate: this.state.deal.dueDate
    }

    if (!this.state.deal.clients) {
      this.setState(
        {
          ...this.state,
          deal: {
            ...this.state.deal,
            clients: [client]
          }
        },
        () => {
          this.props.setIsDealFormDirty()
        }
      )
    } else {
      if (value.length < this.state.deal.clients.length) {
        const removedClient = differenceBy(this.state.deal.clients, value, 'value')
        this.removeClientConfirmation(() => {
          const index = this.state.deal.clients.findIndex(
            client => client.value === removedClient[0].value
          )
          state = update(this.state, {
            deal: {
              clients: {
                $splice: [[index, 1]]
              }
            }
          })
          this.setState(state, () => {
            this.props.setIsDealFormDirty()
          })
        }, removedClient)
      } else {
        state = update(this.state, {
          deal: {
            clients: {
              $push: [client]
            }
          }
        })
        this.setState(state, () => {
          this.refreshClientContacts(client.value)
          this.props.setIsDealFormDirty()
        })
      }
    }
  }

  changeClientProperty = (value, property, id) => {
    const index = this.state.deal.clients.findIndex(c => c.value === id)
    const state = update(this.state, {
      deal: {
        clients: {
          [index]: {
            [property]: { $set: value }
          }
        }
      }
    })
    this.setState(state, () => {
      this.props.setIsDealFormDirty()
    })
  }

  changeContactIsMain = (value, id, contactIndex) => {
    const clientIndex = this.state.deal.clients.findIndex(c => c.value === id)
    const state = update(this.state, {
      deal: {
        clients: {
          [clientIndex]: {
            selectedContacts: {
              [contactIndex]: {
                isMain: { $set: value }
              }
            }
          }
        }
      }
    })
    this.setState(state, () => {
      this.props.setIsDealFormDirty()
    })
  }

  changeClientContactRole = (value, id, contactIndex) => {
    const clientIndex = this.state.deal.clients.findIndex(c => c.value === id)
    const state = update(this.state, {
      deal: {
        clients: {
          [clientIndex]: {
            selectedContacts: {
              [contactIndex]: {
                role: { $set: value }
              }
            }
          }
        }
      }
    })
    this.setState(state, () => {
      this.props.setIsDealFormDirty()
    })
  }

  getBidStatus = status => {
    let bidStatus = this.state.deal.bidStatus
    if (typeof this.state.deal.bidStatus === 'object' && this.state.deal.bidStatus !== null) {
      bidStatus = this.state.deal.bidStatus.value
    }

    return [status, bidStatus !== status.value]
  }

  changeBidStatus = async value => {
    let newValue = value
    if (typeof value === 'object') {
      newValue = value.value
    }
    if (this.state.deal.bidStatus.value === newValue) {
      return
    }
    if (newValue === 3) {
      this.props.toggleContractorAwardedModal({ newDealStatus: this.state.deal.status.value })
    } else {
      if (this.state.deal.awardedCompany) {
        const willAgree = await swal({
          title:
            'This action will set the opportunity not awarded to the client you previously selected',
          text: 'Are you sure you want to continue?',
          buttons: ['Cancel', 'Yes'],
          icon: 'warning'
        })
        if (willAgree) {
          this.changeProperties({ bidStatus: value, awardedCompany: null })
          this.setState({
            dealAwarded: false
          })
        }
      } else {
        this.changeProperty(value, 'bidStatus')
      }
    }
  }

  setStateWrapper = state => {
    this.setState(state)
  }

  toggleFollowupByEmailModal = () => {
    this.setState({
      showFollowUpByEmailModal: !this.state.showFollowUpByEmailModal
    })
  }

  deleteDeal = async () => {
    const sweetAlertFactory = withSweetAlert(
      <div>
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
        All documents, proposals, comments, notes & tasks related to this opportunity will be
        permanently deleted.
      </div>
    )
    sweetAlertFactory(
      {
        title: `Are you sure you want to delete this deal?`,
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      () => {
        this.props.deleteDeal()
      },
      { cancelConfirmation: false }
    )
  }

  toggleCloneOpportunityModal = () => {
    this.setState(prevState => ({
      showCloneOpportunityModal: !prevState.showCloneOpportunityModal
    }))
  }

  toggleLinkOpportunityModal = () => {
    this.setState(prevState => ({
      showLinkOpportunityModal: !prevState.showLinkOpportunityModal
    }))
  }

  render() {
    const {
      dealAwarded,
      deal,
      showDealFollowup,
      showHealthError,
      showFollowUpByEmailModal,
      showCloneOpportunityModal,
      showLinkOpportunityModal,
      usingQuoteNumberAutomation,
      usingProjectNumberAutomation
    } = this.state

    const { orderedStatuses } = this.props

    return (
      <>
        <EnhancedDetails
          isPristine={this.props.isDealFormPristine}
          saveChanges={this.onSave}
          dealAwarded={dealAwarded}
          showDealFollowup={showDealFollowup}
          users={this.props.users}
          deal={deal}
          dealInternalTeam={this.props.deal.internalTeam}
          internalTeam={this.state.internalTeam}
          addInternalTeamUser={this.props.addInternalTeamUser}
          removeInternalTeamUser={this.props.removeInternalTeamUser}
          dealCategories={this.props.dealCategories}
          dealWorkTypes={this.props.dealWorkTypes}
          dealBidStatuses={this.props.dealBidStatuses}
          internalBidStatuses={this.props.internalBidStatuses}
          dealProjectStatuses={this.props.dealProjectStatuses}
          dealNotBiddingReasons={this.props.dealNotBiddingReasons}
          dealReasons={this.props.dealReasons}
          dealSources={this.props.dealSources}
          dealScopes={this.props.dealScopes}
          updateState={this.updateState}
          onSelectCallback={this.onSelectCallback}
          onSave={this.onSave}
          orderedStatuses={orderedStatuses}
          showHealthError={showHealthError}
          changeProperty={this.changeProperty}
          changeFollowUp={this.changeFollowUp}
          changeClientContacts={this.changeClientContacts}
          refreshClientContacts={this.refreshClientContacts}
          changeClientProperty={this.changeClientProperty}
          changeContactIsMain={this.changeContactIsMain}
          changeClientContactRole={this.changeClientContactRole}
          state={this.state}
          setState={this.setStateWrapper}
          updateDealStatusFollowupDate={this.updateDealStatusFollowupDate}
          toggleFollowupDateModal={this.toggleFollowupDateModal}
          changeStatus={this.changeStatus}
          changeBidStatus={this.changeBidStatus}
          toggleHealthErrorModal={this.toggleHealthErrorModal}
          getBidStatus={this.getBidStatus}
          changeProperties={this.changeProperties}
          scheduleTaskForAwardedCompany={this.scheduleTaskForAwardedCompany}
          toggleFollowupByEmailModal={this.toggleFollowupByEmailModal}
          deleteDeal={this.deleteDeal}
          toggleCloneOpportunityModal={this.toggleCloneOpportunityModal}
          toggleLinkOpportunityModal={this.toggleLinkOpportunityModal}
          generateQuoteNumber={this.generateQuoteNumber}
          generateProjectNumber={() => this.generateProjectNumber(this.props.dispatch)}
          usingQuoteNumberAutomation={usingQuoteNumberAutomation}
          usingProjectNumberAutomation={usingProjectNumberAutomation}
          settings={this.props.settings}
          geographicalAreas={this.props.geographicalAreas}
          dealContractTypes={this.props.dealContractTypes}
          buildingOwners={this.props.buildingOwners}
          endUsers={this.props.endUsers}
          propertyManagers={this.props.propertyManagers}
          changeSpecialClientsProperty={this.changeSpecialClientsProperty}
        />
        {showFollowUpByEmailModal && (
          <FollowupModalContainer
            showFollowupModal={showFollowUpByEmailModal}
            toggleFollowupModal={this.toggleFollowupByEmailModal}
            selectedCards={[deal]}
          />
        )}
        {showCloneOpportunityModal && (
          <CloneOpportunityModal
            showCloneOpportunityModal={this.state.showCloneOpportunityModal}
            toggleCloneOpportunityModal={this.toggleCloneOpportunityModal}
            dealName={deal.name}
            dueDate={deal.dueDate}
            followUpDate={deal.followUpDate}
            dealManager={deal.dealManager}
            users={this.props.users}
            orderedStatuses={orderedStatuses}
          />
        )}
        {showLinkOpportunityModal && (
          <LinkOpportunityModal
            showLinkOpportunityModal={this.state.showLinkOpportunityModal}
            toggleLinkOpportunityModal={this.toggleLinkOpportunityModal}
          />
        )}
      </>
    )
  }
}

export default DealDetails
